import React, { useState, useEffect } from "react";
import styles from "./ContactSyncPopup.module.css";
import {
  getGoogleSyncStatus,
  triggerGoogleContactsSync,
  syncNativeContacts,
  updateContactSyncPermission,
  isSyncContactSkip,
} from "../../Store/apiStore";
import { requestNativePermission } from "../../utils/nativePermissions";
import { trackEvent } from "../../lib/analytics";

const ContactSyncPopup = ({ isOpen, onClose, onSyncComplete }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [syncError, setSyncError] = useState("");

  // Listen for native contacts from React Native bridge
  useEffect(() => {
    if (!isOpen) return;

    const handleRNMessage = async (event) => {
      try {
        const msg =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!msg?.__fromRN && !msg?.type) return;

        if (msg.type === "CONTACTS_SYNC_START") {
          setSyncing(true);
          setSyncMsg("Starting sync...");
          setSyncError("");
        }

        if (msg.type === "CONTACTS_PERMISSION_CHECK") {
          setSyncing(true);
          setSyncMsg("Checking contacts permission...");
          setSyncError("");
        }

        if (msg.type === "CONTACTS_API_START") {
          setSyncing(true);
          setSyncMsg(`Syncing ${msg.data?.count ?? ""} contacts...`);
        }

        if (
          msg.type === "CONTACTS_API_SUCCESS" ||
          msg.type === "CONTACTS_SYNCED"
        ) {
          setSyncing(false);
          setSyncMsg("");
          handleDone();
        }

        if (msg.type === "CONTACTS_DATA" && Array.isArray(msg.contacts)) {
          setSyncing(true);
          setSyncMsg("Syncing phone contacts...");
          try {
            await syncNativeContacts(msg.contacts);
            handleDone();
          } catch {
            setSyncing(false);
            setSyncError("Phone contacts sync failed. Please try again.");
          }
        }

        if (msg.type === "GOOGLE_AUTH_SUCCESS") {
          // Google Contacts authorised in the native app: the shell posts this
          // after the com.rexptin://contacts deep link. Same event as the web
          // ?google_sync=success handlers; the once-per-user guard dedupes.
          trackEvent("google_contacts_connected", { source: "native_app" });
          setSyncMsg("Syncing Google Contacts...");
          try {
            await triggerGoogleContactsSync();
            handleDone();
          } catch {
            setSyncing(false);
            setSyncError("Google sync failed. Please try again.");
          }
        }

        if (
          msg.type === "GOOGLE_AUTH_ERROR" ||
          msg.type === "GOOGLE_AUTH_CANCELLED"
        ) {
          setSyncing(false);
          setSyncMsg("");
          setSyncError(
            msg.data?.message || "Google login cancelled or failed."
          );
        }

        if (
          msg.type === "CONTACTS_API_ERROR" ||
          msg.type === "CONTACTS_API_FAIL" ||
          msg.type === "CONTACTS_PERMISSION_DENIED" ||
          msg.type === "CONTACTS_ERROR"
        ) {
          setSyncing(false);
          setSyncMsg("");
          setSyncError(msg.data?.message || "Sync failed. Please try again.");
        }
      } catch (e) {
        console.error("[ContactSyncPopup] RN message parse error:", e);
      }
    };

    window.addEventListener("message", handleRNMessage);
    document.addEventListener("message", handleRNMessage);

    return () => {
      window.removeEventListener("message", handleRNMessage);
      document.removeEventListener("message", handleRNMessage);
    };
  }, [isOpen]);

  // Reset visibility after OAuth tab switch (web)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && syncing) {
        setSyncing(false);
        setSyncMsg("");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [syncing]);

  const handleDone = async () => {
    setSyncing(false);
    setSyncMsg("");
    setSyncError("");
    try {
      await isSyncContactSkip();
    } catch (err) {
      console.error("[ContactSyncPopup] sync skip update error:", err);
    }
    sessionStorage.removeItem("isAgentCreated");
    onSyncComplete?.();
    onClose();
  };

  // ── Google Contacts ──────────────────────────────────────────────────────
  const handleGoogleClick = async () => {
    setSyncing(true);
    setSyncMsg("Checking Google connection...");
    setSyncError("");
    try {
      const isRN = !!window.ReactNativeWebView;
      const status = await getGoogleSyncStatus({
        isMobile: isRN,
        flow: "contacts",
      });

      if (status.hasToken) {
        setSyncMsg("Syncing Google Contacts...");
        try {
          await triggerGoogleContactsSync();
          // Already authorised (token shared with Calendar / Google sign-up) →
          // direct sync with no OAuth round-trip, which was never tracked.
          trackEvent("google_contacts_connected", { source: "dashboard_popup" });
          handleDone();
        } catch {
          setSyncing(false);
          setSyncError("Google sync failed. Please try again.");
        }
      } else if (status.authUrl) {
        if (isRN) {
          setSyncMsg("Opening Google login...");
          // flow:"contacts" → App.tsx opens InAppBrowser with
          // redirectUrl=com.rexptin://contacts so the deep link
          // properly auto-closes the browser and returns here.
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              type: "OPEN_GOOGLE_AUTH",
              authUrl: status.authUrl,
              flow: "contacts",
            })
          );
        } else {
          setSyncMsg("Redirecting to Google...");
          window.location.href = status.authUrl;
        }
      } else {
        setSyncError("Could not connect to Google. Please try again.");
        setSyncing(false);
      }
    } catch {
      setSyncError("Connection check failed. Please try again.");
      setSyncing(false);
    }
  };

  // ── Phone Contacts ───────────────────────────────────────────────────────
  const handlePhoneClick = async () => {
    // React Native bridge
    if (window.ReactNativeWebView) {
      // Branded contacts soft-ask + OS permission first (native renders the
      // copy). Only if granted do we ask native to read & sync the contacts —
      // this avoids a second OS prompt because native already holds it.
      const status = await requestNativePermission("CONTACTS", { app: "Rexpt" });
      if (status !== "granted" && status !== "unavailable") return;

      // Flip the spinner on immediately — without this the popup looks
      // frozen for the few hundred ms between this postMessage and the
      // first CONTACTS_PERMISSION_CHECK / CONTACTS_SYNC_START that RN
      // sends back. The listener above takes over once messages start.
      setSyncing(true);
      setSyncMsg("Opening your contacts…");
      setSyncError("");
      const token = localStorage.getItem("token");
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ type: "REQUEST_CONTACTS", token })
      );
      return;
    }

    // Browser Contact Picker API (Chrome mobile)
    if ("contacts" in navigator && "ContactsManager" in window) {
      try {
        const props = ["name", "tel", "email"];
        const contacts = await navigator.contacts.select(props, {
          multiple: true,
        });
        if (contacts && contacts.length > 0) {
          setSyncing(true);
          setSyncMsg("Syncing phone contacts...");
          const formatted = contacts.map((c) => ({
            name: c.name?.[0] || "",
            phone: c.tel?.[0] || "",
            email: c.email?.[0] || "",
            source: "phone_picker",
          }));
          try {
            await syncNativeContacts(formatted);
            handleDone();
          } catch {
            setSyncing(false);
            setSyncError("Phone contacts sync failed.");
          }
        }
      } catch (e) {
        if (e.name !== "TypeError") {
          console.error("[ContactSyncPopup] Contact Picker error:", e);
        }
      }
      return;
    }

    setSyncError("Phone contacts not available on this device.");
  };

  // ── Skip ─────────────────────────────────────────────────────────────────
  const handleSkip = async () => {
    try {
      await isSyncContactSkip();
    } catch (err) {
      console.error("[ContactSyncPopup] skip permission update error:", err);
    }
    sessionStorage.removeItem("isAgentCreated");
    onClose();
  };

  if (!isOpen) return null;

  const showPhoneOption = !!window.ReactNativeWebView;

  return (
    <div className={styles.overlay}>
      <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.iconWrap}>
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"
                stroke="#6524eb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="9"
                cy="7"
                r="4"
                stroke="#6524eb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23 21v-2a4 4 0 0 0-3-3.87"
                stroke="#6524eb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 3.13a4 4 0 0 1 0 7.75"
                stroke="#6524eb"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h3 className={styles.title}>Sync Your Contacts</h3>
          <p className={styles.subtitle}>
            Import your contacts so your AI agent can recognize callers and
            provide personalized service.
          </p>
        </div>

        {/* Status messages */}
        {syncing && (
          <div className={styles.statusBar}>
            <span className={styles.spinnerSmall} />
            {syncMsg || "Please wait..."}
          </div>
        )}
        {syncError && (
          <div className={styles.errorBar}>
            {syncError}
          </div>
        )}

        {/* Options */}
        <div
          className={styles.options}
          style={syncing ? { opacity: 0.5, pointerEvents: "none" } : {}}
        >
          {/* Google Contacts */}
          <button className={styles.optionBtn} onClick={handleGoogleClick}>
            <span className={styles.optionIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            </span>
            <div className={styles.optionText}>
              <p className={styles.optionLabel}>Google Contacts</p>
              <p className={styles.optionDesc}>Sync from your Google account</p>
            </div>
            <svg
              className={styles.chevron}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M9 18l6-6-6-6"
                stroke="#aaa"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>

          {/* Phone Contacts — only on mobile */}
          {showPhoneOption && (
            <button className={styles.optionBtn} onClick={handlePhoneClick}>
              <span className={styles.optionIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="5"
                    y="2"
                    width="14"
                    height="20"
                    rx="2"
                    stroke="#6524eb"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle cx="12" cy="17" r="1" fill="#6524eb" />
                </svg>
              </span>
              <div className={styles.optionText}>
                <p className={styles.optionLabel}>Phone Contacts</p>
                <p className={styles.optionDesc}>Import from your device</p>
              </div>
              <svg
                className={styles.chevron}
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M9 18l6-6-6-6"
                  stroke="#aaa"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Skip button */}
        <button
          className={styles.skipBtn}
          onClick={handleSkip}
          disabled={syncing}
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default ContactSyncPopup;
